import { useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RowTypesIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectFlatVariableNodes, selectVariablesByTemplate } from 'components/editor/grid/reduxStore/allVariableSlice';
import { getDefaultTableContent } from '../defaultData';
import type { VariableEntity, VariableNode } from 'services/entities/VariablesEntity';
import useClickToAddTable from 'components/editor/hooks/useClickToAddTable';
import { DocumentSettingsContext } from 'components/editor/SidePanel/DocumentSettings/DocumentDesignSettings/DocumentSettingsContext';
import { getCanvasWidth } from 'components/editor/shared/gridConfig';
import { useEffectiveOrientation } from 'hooks/useEffectiveOrientation';
import { useTableTotals } from '../useTableTotals';
import type { CrmInstanceResolver } from './deriveCrmRows';
import { buildAuthoredRowPatch, deriveCrmRows } from './deriveCrmRows';
import { resolveArrayFieldsForVariable } from './useCrmArrayVariablePaste';

// Clicking an array variable outside a table creates a new table pre-populated with its rows,
// reusing the same row-generation logic as pasting into an existing table — the whole thing
// ships as one CreateBlockCommand, so it's a single undo step.
export function useInsertArrayVariableAsTable(): (variable: VariableEntity | VariableNode) => Promise<void> {
  const { t } = useTranslation();
  const { documentSettings } = useContext(DocumentSettingsContext);
  const documentSettingsRef = useRef(documentSettings);
  documentSettingsRef.current = documentSettings;
  const canvasWidth = getCanvasWidth(useEffectiveOrientation());
  const canvasWidthRef = useRef(canvasWidth);
  canvasWidthRef.current = canvasWidth;

  const clickToAddTable = useClickToAddTable();
  const getTableTotals = useTableTotals();
  const flatVariableNodes = useAppSelector(selectFlatVariableNodes);
  const variablesByTemplate = useAppSelector(selectVariablesByTemplate);

  return useCallback(
    async (variable: VariableEntity | VariableNode) => {
      const resolved = resolveArrayFieldsForVariable({ variable, flatVariableNodes, variablesByTemplate, t });
      if (!resolved) return;
      const { fields, grouped, getFieldInstances } = resolved;

      const { margin } = documentSettingsRef.current;
      const availableWidth = canvasWidthRef.current - margin.left - margin.right;
      const baseTableContent = getDefaultTableContent(availableWidth);

      const firstBodyRow = baseTableContent.rows.find((row) => row.rowType === RowTypesIdentifier.BODY);
      if (!firstBodyRow) return;

      const patch = buildAuthoredRowPatch({
        row: firstBodyRow,
        columns: baseTableContent.columns,
        field: 'column1',
        fields,
        grouped,
      });

      const rowsForDerive = baseTableContent.rows.map((row) => (row.id === firstBodyRow.id ? { ...row, ...patch } : row));

      const payloadInstancesByTemplate = new Map(fields.map((f) => [f.template, f.instances]));
      const resolve: CrmInstanceResolver = (fieldRef) => {
        const fromPayloadTemplate = payloadInstancesByTemplate.get(fieldRef.name);
        if (fromPayloadTemplate && fromPayloadTemplate.length > 0) return fromPayloadTemplate;
        return getFieldInstances(fieldRef.name);
      };

      const derivedRows = deriveCrmRows(rowsForDerive, baseTableContent.columns, resolve);
      const { rows, totalRows, metadata } = getTableTotals(derivedRows, baseTableContent.columns, baseTableContent);

      await clickToAddTable({
        isPricingTable: false,
        contentOverride: { ...baseTableContent, rows, totalRows, metadata },
      });
    },
    [flatVariableNodes, variablesByTemplate, t, clickToAddTable, getTableTotals]
  );
}
